import { ELECTRIC_TOLLS } from './invoiceV2';
import { invoicePeriod } from './invoiceReview';

const number = value => {
    if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};
const cents = value => Math.round((value + Number.EPSILON) * 100);
const money = value => cents(value) / 100;

function periodCost(quantities, prices, count) {
    let total = 0;
    for (let i = 1; i <= count; i += 1) {
        const period = `P${i}`, quantity = number(quantities?.[period]);
        if (quantity == null) return null;
        if (quantity === 0) continue;
        const price = number(prices?.[period]);
        if (price == null) return null;
        total += quantity * price;
    }
    return total;
}

// Presentation only: recover an omitted reference breakdown from the saved
// comparison inputs, never replacing its reference total or recalculating offers.
export function invoiceReferenceBreakdown(result) {
    if (result.desglose_referencia != null) return result.desglose_referencia;
    const data = result.reviewed_input || {};
    if (result.modelo_calculo !== 'comparador_actual' || !ELECTRIC_TOLLS.includes(data.tarifa_atr)) return null;
    const days = number(data.dias_intervalo);
    if (!Number.isInteger(days) || days < 1 || days > 366 || !['eur_kw_dia', 'eur_kw_ano'].includes(data.unidad_precio_potencia)) return null;
    const reference = number(result.total_referencia_eur), reviewedTotal = number(data.total_factura_eur);
    if (reference == null || reviewedTotal == null || cents(reference) !== cents(reviewedTotal)) return null;
    const energy = periodCost(data.consumos_kwh, data.precios_energia_eur_kwh, data.tarifa_atr === '2.0TD' ? 3 : 6);
    const dailyPower = periodCost(data.potencias_kw, data.precios_potencia, data.tarifa_atr === '2.0TD' ? 2 : 6);
    const power = dailyPower == null ? null : dailyPower * days / (data.unidad_precio_potencia === 'eur_kw_ano' ? 365 : 1);
    const discount = number(data.total_descuentos_eur), social = number(data.bono_social_eur), rental = number(data.alquiler_eur);
    const ie = number(data.ie_pct), vat = number(data.iva_pct), surplusKwh = number(data.excedentes_kwh);
    if ([energy, power, discount, social, rental, ie, vat, surplusKwh].some(value => value == null) || ie > 100 || vat > 100) return null;
    const surplusPrice = surplusKwh === 0 ? 0 : number(data.precio_excedentes_eur_kwh);
    if (surplusPrice == null) return null;
    const surplus = Math.min(energy, surplusKwh * surplusPrice);
    const net = energy + power - surplus - discount;
    if (net < 0) return null;
    // Same bases as the current comparator: social funding pays IE and VAT;
    // meter rental pays VAT only. Keep precision until displaying each amount.
    const base = net + social;
    const electricTax = base * ie / 100;
    const totalVat = (base + electricTax + rental) * vat / 100;
    const total = base + electricTax + rental + totalVat;
    if (!Number.isFinite(total) || cents(total) !== cents(reference)) return null;
    const breakdown = {
        coste_potencia: money(power), coste_energia: money(energy), descuento: money(discount),
        termino_excedentes: money(surplus), bono_social: money(social), alquiler: money(rental),
        impuesto_electrico: money(electricTax), total_iva: money(totalVat), total_factura: money(total),
    };
    // A matching total alone must not hide a rounding difference in the rows.
    const displayedTotal = breakdown.coste_potencia + breakdown.coste_energia - breakdown.descuento - breakdown.termino_excedentes
        + breakdown.bono_social + breakdown.alquiler + breakdown.impuesto_electrico + breakdown.total_iva;
    return cents(displayedTotal) === cents(reference) ? breakdown : null;
}

// A missing tax or a total mismatch must not erase independently known costs.
// Keep this separate from the strictly reconciled breakdown above.
export function invoiceReferenceDetails(result) {
    const verified = invoiceReferenceBreakdown(result);
    if (verified) return { values: verified, verified: true, derived: result.desglose_referencia == null, difference: null };
    const data = result.reviewed_input || {};
    if (result.modelo_calculo !== 'comparador_actual' || !ELECTRIC_TOLLS.includes(data.tarifa_atr)) return { values: {}, verified: false, difference: null };
    const count = data.tarifa_atr === '2.0TD' ? 3 : 6;
    const days = number(data.dias_intervalo ?? data.dias_facturados ?? invoicePeriod(data).days);
    const energy = periodCost(data.consumos_kwh, data.precios_energia_eur_kwh, count);
    const daily = periodCost(data.potencias_kw, data.precios_potencia, count === 3 ? 2 : 6);
    const power = daily != null && Number.isInteger(days) && days >= 1 && days <= 366 && ['eur_kw_dia', 'eur_kw_ano'].includes(data.unidad_precio_potencia)
        ? daily * days / (data.unidad_precio_potencia === 'eur_kw_ano' ? 365 : 1) : null;
    const excess = number(data.excedentes_kwh), excessPrice = number(data.precio_excedentes_eur_kwh);
    const surplus = excess === 0 ? 0 : energy != null && excess != null && excessPrice != null ? Math.min(energy, excess * excessPrice) : null;
    const discount = number(data.total_descuentos_eur), social = number(data.bono_social_eur), rental = number(data.alquiler_eur);
    const ie = number(data.ie_pct), vat = number(data.iva_pct);
    const base = [energy, power, surplus, discount, social].every(value => value != null) ? energy + power - surplus - discount + social : null;
    const tax = base != null && base >= 0 && ie != null && ie <= 100 ? base * ie / 100 : null;
    const totalVat = tax != null && rental != null && vat != null && vat <= 100 ? (base + tax + rental) * vat / 100 : null;
    const raw = { coste_potencia: power, coste_energia: energy, termino_excedentes: surplus, descuento: discount,
        bono_social: social, alquiler: rental, impuesto_electrico: tax, total_iva: totalVat };
    const values = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, value == null || !Number.isFinite(value) ? null : money(value)]));
    const reference = number(result.total_referencia_eur);
    const known = Object.values(values).every(value => value != null);
    const subtotal = known ? values.coste_potencia + values.coste_energia - values.termino_excedentes - values.descuento
        + values.bono_social + values.alquiler + values.impuesto_electrico + values.total_iva : null;
    return { values, verified: false, derived: true, difference: reference != null && subtotal != null ? money(reference - subtotal) : null };
}
